// ============================================================================
// User menu › «Mis empresas»: the accounts this browser has signed in to, one
// click to switch, and «Añadir otra empresa» (sign in to another account).
// lib/accounts.js keeps the sessions; nothing changes server-side.
// ============================================================================
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Plus, Loader2, X, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { listAccounts, rememberCurrent, switchTo, forget, signInAndRemember } from '../lib/accounts';
import { supabase } from '../lib/supabase';
import '../locales/parity';

export default function AccountSwitcher({ user, companyName, onNavigate }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!live) return;
      setAccounts(data?.session ? rememberCurrent(data.session, companyName) : listAccounts());
    }).catch(() => { if (live) setAccounts(listAccounts()); });
    return () => { live = false; };
  }, [user?.id, companyName]);

  const others = accounts.filter((a) => a.user_id !== user?.id);

  const doSwitch = async (acc) => {
    setBusy(acc.user_id); setError('');
    const r = await switchTo(acc.user_id);
    if (r.ok) { window.location.assign('/dashboard'); return; }
    setBusy('');
    if (r.expired) { setEmail(acc.email); setError(t('accounts.expired', { email: acc.email })); setOpen(true); }
    else setError(t('accounts.error', { message: r.error || '' }));
  };

  const doAdd = async () => {
    setBusy('add'); setError('');
    const r = await signInAndRemember(email.trim(), password);
    if (r.ok) { window.location.assign('/dashboard'); return; }
    setBusy('');
    setError(t('accounts.error', { message: r.error || '' }));
  };

  return (
    <div className="border-t border-[#e1e5e8] py-1" data-testid="account-switcher">
      <p className="px-5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#6d7d8b] flex items-center gap-1.5"><Building2 className="w-3 h-3" /> {t('accounts.title')}</p>
      <div className="px-5 py-1.5 text-sm text-[#1d2d3d] flex items-center justify-between gap-2">
        <span className="truncate font-medium">{companyName || user?.email}</span>
        <span className="text-[10px] font-bold uppercase text-[#2e7d32] flex items-center gap-1"><Check className="w-3 h-3" /> {t('accounts.current')}</span>
      </div>
      {others.map((a) => (
        <div key={a.user_id} className="px-5 py-1.5 text-sm flex items-center justify-between gap-2 hover:bg-blue-50 group">
          <button type="button" onClick={() => doSwitch(a)} disabled={!!busy} className="flex-1 text-left truncate text-[#6d7d8b] group-hover:text-[#1a4f8b] font-medium disabled:opacity-60" title={a.email} data-testid="account-switch">
            {busy === a.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}{a.company || a.email}
            <span className="block text-[11px] text-gray-400 truncate">{a.email}</span>
          </button>
          <button type="button" onClick={() => setAccounts(forget(a.user_id))} className="text-gray-300 hover:text-[#c62828]" title={t('accounts.remove')}><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button type="button" onClick={() => { setError(''); setOpen(true); }} className="w-full text-left px-5 py-2 text-sm text-[#1a4f8b] font-semibold hover:bg-blue-50 flex items-center gap-2" data-testid="account-add">
        <Plus className="w-4 h-4" /> {t('accounts.add')}
      </button>
      {error && !open && <p className="px-5 pb-2 text-xs text-[#c62828]">{error}</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="account-add-dialog">
          <DialogHeader><DialogTitle>{t('accounts.add')}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6d7d8b]">{t('accounts.addHint')}</p>
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="block text-xs font-semibold text-[#6d7d8b] mb-1">{t('accounts.email')}</span>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-semibold text-[#6d7d8b] mb-1">{t('accounts.password')}</span>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" onKeyDown={(e) => { if (e.key === 'Enter' && email && password) doAdd(); }} />
            </label>
            {error && <p className="text-sm text-[#c62828]" role="alert">{error}</p>}
            <p className="text-xs text-[#6d7d8b]">
              {t('accounts.createNewHint')}{' '}
              <button type="button" className="text-[#1a4f8b] font-semibold hover:underline" onClick={() => { setOpen(false); onNavigate?.('/auth?mode=signup'); }}>{t('accounts.createNew')}</button>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy === 'add'}>{t('common.cancel')}</Button>
            <Button onClick={doAdd} disabled={busy === 'add' || !email || !password} className="bg-[#1a4f8b] hover:bg-[#123963] text-white">
              {busy === 'add' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{busy === 'add' ? t('accounts.signingIn') : t('accounts.signIn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
